import { useState, useRef, useCallback, useEffect } from 'react';
import * as apiClient from '../lib/api';
import {
  X,
  Download,
  FileText,
  PenTool,
  Type,
  Image as ImageIcon,
  Save,
  Trash2,
  Eraser,
  ChevronLeft,
} from 'lucide-react';

interface Workbook {
  id: string;
  name: string;
  description?: string;
  type: 'pdf' | 'video' | 'guide' | 'template';
  fileUrl: string;
  bookId?: string;
}

interface WorkbookWorkspaceProps {
  workbook: Workbook;
  userId: string;
  onClose: () => void;
}

type Tool = 'text' | 'draw' | 'erase';

interface DrawnPath {
  points: { x: number; y: number }[];
  color: string;
  width: number;
}

export default function WorkbookWorkspace({ workbook, userId, onClose }: WorkbookWorkspaceProps) {
  // Notes state
  const [noteContent, setNoteContent] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [activeTool, setActiveTool] = useState<Tool>('text');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawnPaths, setDrawnPaths] = useState<DrawnPath[]>([]);
  const [currentPath, setCurrentPath] = useState<DrawnPath | null>(null);
  const [brushColor, setBrushColor] = useState('#dc2626');
  const brushWidth = 3;
  const notesPanelWidth = 400;
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Load existing workbook note via REST
  useEffect(() => {
    apiClient.workbook.get(workbook.id).then((note) => {
      if (note) {
        setNoteContent(note.content || '');
        setImages(note.images || []);
        if (note.drawings) {
          setDrawnPaths(JSON.parse(note.drawings));
        }
      }
    }).catch(() => {});
  }, [workbook.id]);

  // Redraw canvas when paths change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawnPaths.forEach(path => {
      if (path.points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = path.color;
      ctx.lineWidth = path.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(path.points[0].x, path.points[0].y);
      path.points.slice(1).forEach(point => {
        ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
    });
  }, [drawnPaths]);

  // Handle image drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setImages(prev => [...prev, event.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Handle image paste
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    items.forEach(item => {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            if (event.target?.result) {
              setImages(prev => [...prev, event.target!.result as string]);
            }
          };
          reader.readAsDataURL(file);
        }
      }
    });
  }, []);

  // Handle file input
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setImages(prev => [...prev, event.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Drawing handlers
  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (activeTool === 'text') return;
    const coords = getCanvasCoords(e);
    setIsDrawing(true);
    setCurrentPath({
      points: [coords],
      color: activeTool === 'erase' ? '#ffffff' : brushColor,
      width: activeTool === 'erase' ? 20 : brushWidth,
    });
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !currentPath || activeTool === 'text') return;
    const coords = getCanvasCoords(e);
    setCurrentPath(prev => prev ? {
      ...prev,
      points: [...prev.points, coords],
    } : null);

    // Draw in real-time
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const points = currentPath.points;
    if (points.length < 1) return;

    ctx.beginPath();
    ctx.strokeStyle = currentPath.color;
    ctx.lineWidth = currentPath.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.moveTo(points[points.length - 1].x, points[points.length - 1].y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && currentPath) {
      setDrawnPaths(prev => [...prev, currentPath]);
    }
    setIsDrawing(false);
    setCurrentPath(null);
  };

  // Save note
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiClient.workbook.upsert({
        materialId: workbook.id,
        bookId: workbook.bookId,
        content: noteContent,
        images,
        drawings: JSON.stringify(drawnPaths),
      });
      setLastSaved(new Date());
    } catch (error) {
      // Save failed silently
    }
    setIsSaving(false);
  };

  // Auto-save every 30 seconds if there are changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (noteContent || images.length > 0 || drawnPaths.length > 0) {
        handleSave();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [noteContent, images, drawnPaths]);

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const clearDrawing = () => {
    setDrawnPaths([]);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  return (
    <div className="workbook-workspace" style={{
      display: 'flex',
      height: 'calc(100vh - 180px)',
      minHeight: '500px',
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    }}>
      {/* Workbook Viewer */}
      <div className="workbook-viewer" style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid #e5e7eb',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.375rem',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChevronLeft size={18} color="#666" />
            </button>
            <FileText size={20} color="#ef4444" />
            <div>
              <h2 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: '#0f172a' }}>
                {workbook.name}
              </h2>
              {workbook.description && (
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#666' }}>{workbook.description}</p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => window.open(workbook.fileUrl, '_blank')}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                fontSize: '0.8125rem',
                color: '#374151',
              }}
            >
              <Download size={16} />
              Download
            </button>
          </div>
        </div>

        {/* Workbook Content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <iframe
            src={workbook.fileUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: 'white',
            }}
            title={workbook.name}
          />
        </div>
      </div>

      {/* Notes Panel */}
      <div
        className="workbook-notes-panel"
        style={{
          width: notesPanelWidth,
          minWidth: '280px',
          maxWidth: '450px',
          display: 'flex',
          flexDirection: 'column',
          background: '#fafafa',
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onPaste={handlePaste}
      >
        {/* Notes Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #e5e7eb',
          background: 'white',
        }}>
          <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}>
            My Workspace
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {lastSaved && (
              <span style={{ fontSize: '0.6875rem', color: '#999' }}>
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                padding: '0.375rem 0.625rem',
                background: isSaving ? '#e5e7eb' : 'var(--accent)',
                color: isSaving ? '#999' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isSaving ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              <Save size={14} />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Tools */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          padding: '0.5rem 1rem',
          borderBottom: '1px solid #e5e7eb',
          background: 'white',
        }}>
          <button
            onClick={() => setActiveTool('text')}
            style={{
              padding: '0.5rem',
              background: activeTool === 'text' ? 'var(--accent)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Text"
          >
            <Type size={18} color={activeTool === 'text' ? 'white' : '#666'} />
          </button>
          <button
            onClick={() => setActiveTool('draw')}
            style={{
              padding: '0.5rem',
              background: activeTool === 'draw' ? 'var(--accent)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Draw"
          >
            <PenTool size={18} color={activeTool === 'draw' ? 'white' : '#666'} />
          </button>
          <button
            onClick={() => setActiveTool('erase')}
            style={{
              padding: '0.5rem',
              background: activeTool === 'erase' ? 'var(--accent)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Eraser"
          >
            <Eraser size={18} color={activeTool === 'erase' ? 'white' : '#666'} />
          </button>

          <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 0.5rem' }} />

          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding: '0.5rem',
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Add Image"
          >
            <ImageIcon size={18} color="#666" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {activeTool === 'draw' && (
            <>
              <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 0.5rem' }} />
              {['#dc2626', '#000000', '#3b82f6', '#22c55e', '#f59e0b'].map(color => (
                <button
                  key={color}
                  onClick={() => setBrushColor(color)}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: color,
                    border: brushColor === color ? '2px solid #0f172a' : '2px solid transparent',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </>
          )}

          <div style={{ flex: 1 }} />

          {drawnPaths.length > 0 && (
            <button
              onClick={clearDrawing}
              style={{
                padding: '0.375rem 0.5rem',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                fontSize: '0.6875rem',
                color: '#dc2626',
              }}
            >
              <Trash2 size={12} />
              Clear
            </button>
          )}
        </div>

        {/* Notes Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
          {/* Text Area */}
          {activeTool === 'text' && (
            <textarea
              ref={textAreaRef}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Type your notes here... You can also paste or drag & drop images."
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                resize: 'vertical',
                fontFamily: 'inherit',
                marginBottom: '1rem',
              }}
            />
          )}

          {/* Drawing Canvas */}
          {(activeTool === 'draw' || activeTool === 'erase') && (
            <div style={{
              marginBottom: '1rem',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden',
              background: 'white',
            }}>
              <canvas
                ref={canvasRef}
                width={notesPanelWidth - 34}
                height={300}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                style={{
                  display: 'block',
                  cursor: activeTool === 'erase' ? 'crosshair' : 'crosshair',
                }}
              />
            </div>
          )}

          {/* Images */}
          {images.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem', fontWeight: 600 }}>
                Images ({images.length})
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {images.map((img, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <img
                      src={img}
                      alt={`Note image ${index + 1}`}
                      style={{
                        width: '150px',
                        height: '100px',
                        objectFit: 'cover',
                      }}
                    />
                    <button
                      onClick={() => removeImage(index)}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: 'rgba(0,0,0,0.6)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <X size={12} color="white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Drop Zone Hint */}
          <div style={{
            padding: '2rem',
            border: '2px dashed #e5e7eb',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#999',
            fontSize: '0.8125rem',
          }}>
            <ImageIcon size={24} color="#ccc" style={{ marginBottom: '0.5rem' }} />
            <p style={{ margin: 0 }}>Drag & drop images here</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem' }}>or paste from clipboard</p>
          </div>
        </div>
      </div>
    </div>
  );
}
